import { AssessmentDetailViewParams } from "@/features/assessments/features/answers/types";
import { useSiteAssessmentsTasks } from "@/features/site-assessment/site-assessments-tasks.store";
import { api, useApiV1UserPreferencesUpdateMutation } from "@/services/api";
import { useAppDispatch } from "@/services/store";
import { Loader } from "@/ui-components/Loader";
import { MENU_WIDTH } from "@/ui-components/layouts/constants";
import { DeleteOutlineOutlined, ExpandLess, ExpandMore, FilterAltOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, List, ListItem, ListItemButton, Typography } from "@mui/material";
import React, { useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListItemText,
  ListSubheader,
  StatusBox,
  StatusIcon,
} from "../components/Arrordion";
import { FilterDrawer } from "./FilterDrawer";
import { FILTER_CHIP_I18N_KEYS, FilterChip } from "./components/FilterChip";
import { NeedsReview } from "./components/NeedsReview/NeedsReview";
import { useGetData, usePrefetchCallbacks } from "./hooks";
import { AssessmentMode, AssessmentUserPreference, Filters, Menu, MenuTask } from "./types";
import { DEFAULT_ASSESSMENT_PREFERENCE, NO_STANDARD_ID, groupIsActive, useAssessmentPreferences } from "./utils";

type AssessmentsMenuBaseProps = {
  linkGenerator: (assessmentId: string, siteAssessmentId: string, taskId: string) => string;
  mode: AssessmentMode;
  defaults: AssessmentUserPreference;
  onPreferenceChange: (preference: AssessmentUserPreference) => void;
};

interface State {
  stored: {
    defaults: AssessmentUserPreference;
    tasks: MenuTask[] | undefined;
  };
  filters: Filters;
  expanded: string[];
  showFilters: boolean;
  selectedStandard: string;
}

type Action =
  | { type: "SET_DEFAULTS"; payload: AssessmentUserPreference }
  | { type: "TOGGLE_FILTER_DRAWER" }
  | { type: "SET_EXPANDED"; payload: string[] }
  | { type: "SET_TASKS"; tasks: MenuTask[] }
  | { type: "TOGGLE_PANEL"; payload: string | null | undefined }
  | { type: "SET_STANDARD"; payload: string }
  | { type: "SET_FILTERS"; payload: Filters };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_DEFAULTS":
      return {
        ...state,
        filters: action.payload.filters,
        selectedStandard: action.payload.standard,
        stored: {
          ...state.stored,
          defaults: action.payload,
        },
      };
    case "TOGGLE_FILTER_DRAWER":
      return { ...state, showFilters: !state.showFilters };
    case "SET_EXPANDED":
      return { ...state, expanded: action.payload };
    case "TOGGLE_PANEL": {
      const panel = action.payload;
      if (!panel) {
        return state;
      }
      const expanded = state.expanded.includes(panel)
        ? state.expanded.filter(p => p !== panel)
        : [...state.expanded, panel];
      return { ...state, expanded };
    }
    case "SET_STANDARD":
      return { ...state, selectedStandard: action.payload };
    case "SET_FILTERS":
      return { ...state, filters: action.payload };
    case "SET_TASKS":
      return { ...state, stored: { ...state.stored, tasks: action.tasks } };
    default:
      return state;
  }
}

export const AssessmentsMenuBaseInside: React.FC<AssessmentsMenuBaseProps> = ({
  linkGenerator,
  mode,
  defaults,
  onPreferenceChange,
}) => {
  const { assessmentId, siteAssessmentId, taskId } =
    useParams<AssessmentDetailViewParams>() as AssessmentDetailViewParams;
  const { prefetchTaskCallback } = usePrefetchCallbacks(assessmentId);
  const { t } = useTranslation();
  const setTasksState = useSiteAssessmentsTasks(state => state.setTasksState);

  const [{ filters, expanded, showFilters, selectedStandard, stored }, dispatch] = useReducer(reducer, {
    stored: {
      defaults,
      tasks: undefined,
    },
    filters: defaults.filters,
    expanded: [],
    showFilters: false,
    selectedStandard: defaults.standard,
  } as State);

  if (defaults !== stored.defaults) {
    dispatch({ type: "SET_DEFAULTS", payload: defaults });
  }

  const { tasks, themes, standards, menulist, isLoading } = useGetData(selectedStandard, {
    filters,
    mode,
  });

  useEffect(() => {
    setTasksState({ hasNoData: tasks?.length === 0, isLoading });
  }, [tasks, isLoading, setTasksState]);

  // Sets the default value of _expanded_.
  // there might be a case where after filters are updated, the task should not be visible anymore
  // that's when we use payload of an empty array
  if (stored.tasks !== tasks && taskId) {
    dispatch({ type: "SET_TASKS", tasks });
    const group = menulist?.find(g => g.sub_groups.find(sub_group => sub_group.tasks.some(task => task.id === taskId)));
    const idOfPanelInGroup = group?.sub_groups[0].tasks?.[0]?.id;
    dispatch({ type: "SET_EXPANDED", payload: idOfPanelInGroup ? [idOfPanelInGroup] : [] });
  }

  const toggleMenuItem = (group: Menu) => () => {
    const panel = group.sub_groups[0].tasks?.[0]?.id;
    dispatch({ type: "TOGGLE_PANEL", payload: panel });
  };

  const onStandardChange = (standard: string) => {
    dispatch({ type: "SET_STANDARD", payload: standard });
    onPreferenceChange({
      standard,
      filters,
    });
  };

  const onRemoveFilter = (key: keyof Filters) => {
    const next = { ...filters, [key]: false } as Filters;
    dispatch({ type: "SET_FILTERS", payload: next });
    onPreferenceChange({
      standard: selectedStandard,
      filters: next,
    });
  };

  const onChange = (newFilters: Filters, standard: string) => {
    dispatch({ type: "SET_FILTERS", payload: newFilters });
    dispatch({ type: "SET_STANDARD", payload: standard });
    onPreferenceChange({
      standard,
      filters: newFilters,
    });
  };

  const filtersAreActive = Object.values(filters).some(Boolean) || selectedStandard !== NO_STANDARD_ID;

  if (isLoading || !standards) {
    return <Loader sx={{ width: "250px" }} />;
  }

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        width: MENU_WIDTH,
      }}
    >
      <FilterDrawer
        open={showFilters}
        onClose={() => dispatch({ type: "TOGGLE_FILTER_DRAWER" })}
        filters={filters}
        standards={standards}
        onChange={onChange}
        selectedStandard={selectedStandard}
      />
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--green-grey-scale-100)",
        }}
      >
        <IconButton onClick={() => dispatch({ type: "TOGGLE_FILTER_DRAWER" })}>
          <FilterAltOutlined
            sx={[
              showFilters
                ? {
                    color: "var(--soft-black-gray-scale-900)",
                  }
                : {
                    color: "grey",
                  },
            ]}
          />
        </IconButton>
        <Button
          variant="outlined"
          size="small"
          onClick={() => dispatch({ type: "SET_EXPANDED", payload: [] })}
          sx={{ my: 2, mr: 2 }}
          endIcon={<ExpandLess />}
          disabled={themes && themes.length === 0}
        >
          {t("general.Collapse_all")}
        </Button>
      </Box>
      {filtersAreActive && (
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            m: "16px",
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              onPreferenceChange(DEFAULT_ASSESSMENT_PREFERENCE);
            }}
            sx={{
              borderRadius: "4px",
              background: "var(--Primary-Primary-Mint-Green-200, #00FFBC)",
              display: "flex",
              height: "24px",
              padding: "2px 8px",
              justifyContent: "center",
              alignItems: "center",
              gap: "4px",
              fontSize: "12px",
            }}
            endIcon={<DeleteOutlineOutlined />}
          >
            {t("general.Clear_all")}
          </Button>
          {selectedStandard !== NO_STANDARD_ID && (
            <FilterChip
              label={standards?.find(standard => standard.id === Number(selectedStandard))?.name}
              onDelete={() => onStandardChange(NO_STANDARD_ID)}
            />
          )}
          {FILTER_CHIP_I18N_KEYS.filter(({ field }) => filters[field]).map(({ field, key }) => {
            return <FilterChip key={field} onDelete={() => onRemoveFilter(field)} label={t(key)} />;
          })}
        </Box>
      )}
      {menulist!.map(group => (
        <Accordion key={group.group_id} expanded={groupIsActive(group, expanded)} onChange={toggleMenuItem(group)}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls={`${group.group_id}-content`}
            id={`${group.group_id}-header`}
          >
            <Typography
              sx={{
                fontWeight: groupIsActive(group, expanded) ? "700 !important" : "400",
              }}
            >
              {group.group_name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List
              sx={{
                px: 0,
                py: 0,
              }}
            >
              {group.sub_groups.map(sub_group => (
                <ListItem
                  key={sub_group.sub_group_id}
                  sx={{
                    px: 0,
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <ListSubheader component="div">{sub_group.sub_group_name}</ListSubheader>
                    <List component="div">
                      {sub_group.tasks.map(({ id, status, name, reviewers, is_deleted, requirement_reference }) => (
                        <ListItem
                          component="div"
                          sx={{
                            px: 0,
                            py: 0,
                          }}
                          key={id}
                        >
                          <ListItemButton
                            component={Link}
                            to={linkGenerator(assessmentId, siteAssessmentId, id)}
                            onMouseEnter={prefetchTaskCallback}
                            sx={[
                              {
                                px: 2,
                                paddingRight: "32px",
                              },
                              id === taskId
                                ? {
                                    background: "#E0F2EF",
                                  }
                                : {
                                    background: "unset",
                                  },
                            ]}
                          >
                            <StatusBox>
                              <StatusIcon status={status} />
                            </StatusBox>
                            <ListItemText
                              sx={[
                                is_deleted
                                  ? {
                                      color: "var(--grey-color-400)",
                                    }
                                  : {
                                      color: "inherit",
                                    },
                              ]}
                            >
                              {name} {requirement_reference && <span className="ml-1">({requirement_reference})</span>}
                            </ListItemText>
                            {mode === "reviewer" && <NeedsReview status={status} reviewers={reviewers} />}
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export const AssessmentsMenuBase: React.FC<Omit<AssessmentsMenuBaseProps, "defaults" | "onPreferenceChange">> = ({
  linkGenerator,
  mode,
}) => {
  const dispatch = useAppDispatch();
  const { assessmentId, siteAssessmentId } = useParams<AssessmentDetailViewParams>() as AssessmentDetailViewParams;
  const { isLoading, preference: defaults, tableName } = useAssessmentPreferences(assessmentId, siteAssessmentId);
  const [savePreference] = useApiV1UserPreferencesUpdateMutation();

  const onPreferenceChange = (preference: AssessmentUserPreference) => {
    savePreference({
      tableName,
      userPreference: {
        preference,
      },
    }).then(_ => {
      // I'm unsure why mutation itself is not invalidating the tags, but this fixes
      // the issue of app not re-rendering
      dispatch(api.util.invalidateTags(["api" as const]));
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <AssessmentsMenuBaseInside
      linkGenerator={linkGenerator}
      mode={mode}
      defaults={defaults}
      onPreferenceChange={onPreferenceChange}
    />
  );
};
